import {
  booleanAttribute,
  Component,
  HostBinding,
  HostListener,
  Input,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  CdkConnectedOverlay,
  CdkOverlayOrigin,
  ConnectedPosition,
} from '@angular/cdk/overlay';

// Small "info" icon that reveals a plain-language explanation of the
// component it's attached to - what it shows, its defaults, and when/how
// the value changes - on hover (desktop) or tap (mobile/touch).
//
// The popover is rendered through a CDK Overlay (portaled to
// .cdk-overlay-container at the document body, not a descendant of this
// component's own DOM position) rather than a plain absolutely-positioned
// element. That matters whenever this component sits inside a container
// with `overflow: auto/hidden` (e.g. a horizontally-scrolling table) - a
// plain absolute-positioned popover would get clipped by that ancestor,
// while an overlay-portaled one always renders above everything, the same
// way Angular Material's own tooltips/menus do.
@Component({
  selector: 'app-info-tooltip',
  standalone: true,
  imports: [MatIcon, CdkConnectedOverlay, CdkOverlayOrigin],
  templateUrl: './common-component-info-tooltip.component.html',
  styleUrl: './common-component-info-tooltip.component.scss',
})
export class CommonInfoTooltipComponent {
  @Input() heading = '';
  @Input() description = '';

  // The host element is an inline custom element by default, so it just
  // flows into whatever text precedes it. Pass this (as a bare attribute,
  // like `disabled`) when the icon should sit on its own line below a
  // label (KPI cards, table cells) instead of forcing that with a <br>
  // in every call site - see the host class below.
  @Input({ transform: booleanAttribute })
  @HostBinding('class.info-tooltip-host-block')
  block = false;

  // Which edge of the icon the popover should preferentially hang off. Use
  // 'left' when the icon sits near the left edge of the screen (so the
  // popover opens rightward instead of overflowing off-screen), 'right'
  // near the right edge, and the default 'center' otherwise. This only
  // picks the FIRST position the overlay tries - if there isn't room for
  // it there, the overlay automatically falls back through the rest of
  // `positions` below until one actually fits the viewport.
  @Input() align: 'left' | 'center' | 'right' = 'center';

  isOpen = false;

  // Debounces closing on mouseleave so the pointer can travel from the icon
  // to the popover (a separate element once portaled to the overlay
  // container) without the popover disappearing first - cancelled if the
  // pointer re-enters either the icon or the popover in time.
  private closeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  get positions(): ConnectedPosition[] {
    const below = (
      overlayX: 'start' | 'center' | 'end'
    ): ConnectedPosition => ({
      originX: overlayX,
      originY: 'bottom',
      overlayX,
      overlayY: 'top',
      offsetY: 6,
    });
    const above = (
      overlayX: 'start' | 'center' | 'end'
    ): ConnectedPosition => ({
      originX: overlayX,
      originY: 'top',
      overlayX,
      overlayY: 'bottom',
      offsetY: -6,
    });

    const preferred: 'start' | 'center' | 'end' =
      this.align === 'left'
        ? 'start'
        : this.align === 'right'
          ? 'end'
          : 'center';
    const others = (['start', 'center', 'end'] as const).filter(
      (x) => x !== preferred
    );

    // Try the preferred horizontal alignment below the icon first, then
    // above it, then fall back through the other alignments (below, then
    // above) in case the preferred one doesn't fit anywhere.
    return [
      below(preferred),
      above(preferred),
      ...others.flatMap((x) => [below(x), above(x)]),
    ];
  }

  toggle(event: Event): void {
    event.stopPropagation();
    this.cancelScheduledClose();
    this.isOpen = !this.isOpen;
  }

  open(): void {
    this.cancelScheduledClose();
    this.isOpen = true;
  }

  scheduleClose(): void {
    this.cancelScheduledClose();
    this.closeTimeoutId = setTimeout(() => {
      this.isOpen = false;
    }, 150);
  }

  private cancelScheduledClose(): void {
    if (this.closeTimeoutId !== null) {
      clearTimeout(this.closeTimeoutId);
      this.closeTimeoutId = null;
    }
  }

  @HostListener('document:click')
  onDocumentClick(): void {
    this.isOpen = false;
  }
}
